// One shared journey for every gym. This only carries the gym a person was heading for: someone who arrives
// through a pod's own link or QR code (https://app.bodycodez.com/scan-at/<code>) has that pod's public code
// remembered, so the shared screens can keep that gym in view (the existing Start scan screen preselects it) and
// show gym-specific content only where it applies. It adds no screens and changes no step order.
import { DEVICE_CODE_RE } from "@/lib/scanTarget";

export const POD_ENTRY_KEY = "podEntryCode";
export const POD_ENTRY_TTL_MS = 2 * 60 * 60 * 1000;

/** CrossFit Jozi, whose own challenge opt-in the shared sign-up form can offer. */
export const JOZI_GYM_ID = "de49fff4-1cbf-421d-a623-2b08e2edacba";

export interface PodGym {
  gym_id: string;
  gym_name: string;
  gym_slug: string;
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Where a pod's own link / QR leads for a signed-in member: the Start scan confirmation with that pod already chosen. */
export const scanAtPath = (code: string) => `/scan-at/${code.toLowerCase()}`;

/** A pod's public code from a query value, or null when it is missing or not a device code. */
export function readPodCode(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim();
  return DEVICE_CODE_RE.test(v) ? v.toLowerCase() : null;
}

function safeStorage(): StorageLike | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Remember the pod the person was heading for. */
export function rememberPodEntry(code: string, storage: StorageLike | null = safeStorage(), now: number = Date.now()): void {
  const clean = readPodCode(code);
  if (!clean || !storage) return;
  try {
    storage.setItem(POD_ENTRY_KEY, JSON.stringify({ code: clean, at: now }));
  } catch {
    /* storage unavailable: the shared screens simply behave as they do today */
  }
}

/** The remembered pod if it is valid and recent. Does not clear it. */
export function peekPodEntry(storage: StorageLike | null = safeStorage(), now: number = Date.now()): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(POD_ENTRY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: unknown; at?: unknown };
    if (typeof parsed.at !== "number" || now - parsed.at > POD_ENTRY_TTL_MS || now < parsed.at) {
      storage.removeItem(POD_ENTRY_KEY);
      return null;
    }
    return typeof parsed.code === "string" ? readPodCode(parsed.code) : null;
  } catch {
    return null;
  }
}

/** Forget it once the person has chosen a gym or finished preparing a scan. */
export function clearPodEntry(storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.removeItem(POD_ENTRY_KEY);
  } catch {
    /* nothing to do */
  }
}

export interface SportChoice {
  label: string;
  value: string;
}

/**
 * "Are you doing CrossFit or Hyrox?" For now every gym with a pod is a CrossFit gym, so everyone signing up (through a pod's
 * own QR or link, or from the landing page with no gym chosen) is asked this instead of being shown the long sport menu, and
 * the screen looks the same either way. Both answers save the app's one combined value (the AI report treats them as the same
 * functional-fitness profile). It never names a gym. "Something else" always opens the full menu.
 */
export const CROSSFIT_HYROX_VALUE = "crossfit_hyrox";
export function quickSportChoices(_gym?: PodGym | null): SportChoice[] | null {
  return [
    { label: "CrossFit", value: CROSSFIT_HYROX_VALUE },
    { label: "Hyrox", value: CROSSFIT_HYROX_VALUE },
  ];
}

/**
 * Registration is gym-neutral. Jozi's challenge opt-in is Jozi's content and is offered only where it applies:
 *  - on Jozi's own poster link (?challenge=1);
 *  - when the gym the person is heading for (the pod whose QR or link they came through) is Jozi.
 * The plain sign-up page, where no gym has been chosen, does not offer it. Neither does a visit for another gym,
 * or for a pod whose gym is not known (yet).
 */
export function showJoziChallengeBox(opts: { podCode: string | null; gym: PodGym | null; fromChallenge: boolean }): boolean {
  if (opts.fromChallenge) return true;
  return !!opts.podCode && opts.gym?.gym_id === JOZI_GYM_ID;
}
